/**
 * ESC/POS bytes for an 80 mm receipt printer, from a picture of the slip.
 *
 * The slip is sent as a raster image (GS v 0) rather than as text: the printers
 * we use (Zywell ZY808 and the like) have no UTF-8 mode, so Estonian and Russian
 * letters would come out as junk in text mode. A picture prints whatever the
 * browser can draw.
 *
 * Pure functions, so they are tested without a canvas.
 */

/** 72 mm at 203 dpi: what an 80 mm printer prints across. */
export const RECEIPT_WIDTH_DOTS = 576

/** Rows per GS v 0 command. Some printers drop larger bands. */
export const RASTER_BAND_ROWS = 255

const ESC = 0x1b
const GS = 0x1d

/** ESC @: reset the printer to its defaults. */
export const CMD_INIT = [ESC, 0x40] as const
/** GS V 65 n: feed n lines, then a partial cut. A printer without a cutter only feeds. */
export const cmdFeedAndCut = (feedLines = 3) => [GS, 0x56, 0x41, feedLines & 0xff] as const

/**
 * 1 bit per dot, rows padded to whole bytes, most significant bit first:
 * GS v 0's layout. A dot is black when its luminance is below `threshold`
 * (0–255) and it is not transparent.
 */
export function packMonochrome(
  rgba: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  threshold = 160,
): Uint8Array {
  const rowBytes = Math.ceil(width / 8)
  const out = new Uint8Array(rowBytes * height)
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4
      const alpha = rgba[i + 3]
      if (alpha < 128) continue
      const luminance = 0.299 * rgba[i] + 0.587 * rgba[i + 1] + 0.114 * rgba[i + 2]
      if (luminance < threshold) {
        out[y * rowBytes + (x >> 3)] |= 0x80 >> (x & 7)
      }
    }
  }
  return out
}

/**
 * A complete print job: reset, the picture in bands of RASTER_BAND_ROWS rows,
 * then feed and cut.
 */
export function rasterJob(
  bits: Uint8Array,
  width: number,
  height: number,
  options: { cut?: boolean; feedLines?: number } = {},
): Uint8Array {
  const rowBytes = Math.ceil(width / 8)
  if (bits.length < rowBytes * height) {
    throw new Error(`Raster has ${bits.length} bytes, needs ${rowBytes * height}`)
  }
  const parts: number[] = [...CMD_INIT]
  const chunks: Uint8Array[] = []
  for (let top = 0; top < height; top += RASTER_BAND_ROWS) {
    const rows = Math.min(RASTER_BAND_ROWS, height - top)
    // GS v 0 m xL xH yL yH d…  (m = 0: normal size)
    chunks.push(
      Uint8Array.from([GS, 0x76, 0x30, 0x00, rowBytes & 0xff, rowBytes >> 8, rows & 0xff, rows >> 8]),
      bits.subarray(top * rowBytes, (top + rows) * rowBytes),
    )
  }
  const tail = options.cut === false ? [0x0a, 0x0a, 0x0a] : [...cmdFeedAndCut(options.feedLines ?? 3)]

  const head = Uint8Array.from(parts)
  const end = Uint8Array.from(tail)
  const total = head.length + chunks.reduce((n, c) => n + c.length, 0) + end.length
  const job = new Uint8Array(total)
  let offset = 0
  for (const piece of [head, ...chunks, end]) {
    job.set(piece, offset)
    offset += piece.length
  }
  return job
}

/** Base64 of raw bytes, in slices so a large job doesn't overflow the call stack. */
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  const step = 0x8000
  for (let i = 0; i < bytes.length; i += step) {
    binary += String.fromCharCode(...bytes.subarray(i, i + step))
  }
  return btoa(binary)
}
